import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { squadIdAt, squadsWithIds } from '../utils/teamFixtures';
import { db } from '../firebase';

const GENDERS      = ['Male', 'Female', 'Mixed'];
const ABILITIES    = ['Low', 'Medium', 'High'];
const SQUAD_COUNTS = [1, 2, 3, 4, 5];

const KIT_COLOURS = [
  { name: 'White',    hex: '#FFFFFF', border: true },
  { name: 'Black',    hex: '#111827' },
  { name: 'Grey',     hex: '#6B7280' },
  { name: 'Red',      hex: '#DC2626' },
  { name: 'Blue',     hex: '#1D4ED8' },
  { name: 'Yellow',   hex: '#FBBF24' },
  { name: 'Green',    hex: '#16A34A' },
  { name: 'Orange',   hex: '#EA580C' },
  { name: 'Navy',     hex: '#1e3a5f' },
  { name: 'Maroon',   hex: '#7f1d1d' },
  { name: 'Sky Blue', hex: '#38bdf8' },
  { name: 'Purple',   hex: '#7c3aed' },
  { name: 'Pink',     hex: '#ec4899' },
];

const COLOUR_HEX_MAP = Object.fromEntries(KIT_COLOURS.map((c) => [c.name.toLowerCase(), c.hex]));

function colourToHex(name) {
  if (!name) return null;
  if (name.startsWith('#')) return name;
  return COLOUR_HEX_MAP[name.toLowerCase()] || null;
}

const JERSEY_PATTERNS = [
  { id: 'plain',   label: 'Plain' },
  { id: 'stripes', label: 'Stripes' },
  { id: 'hoops',   label: 'Hoops' },
  { id: 'halves',  label: 'Half & Half' },
];

function patternPreviewStyle(pattern, c1hex, c2hex) {
  const a = c1hex || '#E5E7EB';
  const b = c2hex || '#FFFFFF';
  switch (pattern) {
    case 'stripes': return { background: `repeating-linear-gradient(90deg, ${a} 0px, ${a} 5px, ${b} 5px, ${b} 10px)` };
    case 'hoops':   return { background: `repeating-linear-gradient(0deg,  ${a} 0px, ${a} 5px, ${b} 5px, ${b} 10px)` };
    case 'halves':  return { background: `linear-gradient(90deg, ${a} 50%, ${b} 50%)` };
    default:        return { backgroundColor: a };
  }
}

// Squads are identified by position (squad-1, squad-2…): this form can't read
// what was saved before, so a resubmission must land on the same ids to keep
// each squad's team on the Teams tab (see utils/teamFixtures.js).
function blankSquad(i) {
  return {
    id: squadIdAt(i),
    label: `Squad ${i + 1}`,
    gender: '', yearsOfBirth: [], playersPerMatch: '', abilityLevel: '',
    onlyOneKit: false,
    homeJerseyColour: '', homeJerseyColour2: '', homeJerseyPattern: 'plain',
    homeShortsColour: '', homeSocksColour: '',
    awayJerseyColour: '', awayJerseyColour2: '', awayJerseyPattern: 'plain',
    awayShortsColour: '', awaySocksColour: '',
  };
}

function Label({ children, required }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ value, onChange, type = 'text', placeholder = '', ...rest }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      {...rest}
    />
  );
}

function ColourInput({ value, onChange, label, required }) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {KIT_COLOURS.map((c) => (
          <button
            key={c.name}
            type="button"
            title={c.name}
            onClick={() => onChange(c.name)}
            className={`w-9 h-9 rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] focus:ring-offset-1 ${
              value === c.name ? 'ring-2 ring-[#1A4A8A] ring-offset-1 scale-110' : ''
            } ${c.border ? 'border border-[#D1D5DB]' : ''}`}
            style={{ backgroundColor: c.hex }}
            aria-label={c.name}
            aria-pressed={value === c.name}
          />
        ))}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Blue"
        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      />
    </div>
  );
}

function PatternSelector({ value, onChange, colour1, colour2 }) {
  const c1 = colourToHex(colour1) || '#E5E7EB';
  const c2 = colourToHex(colour2) || '#FFFFFF';
  return (
    <div>
      <Label>Jersey pattern</Label>
      <div className="flex gap-2 flex-wrap">
        {JERSEY_PATTERNS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            aria-pressed={value === p.id}
            className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-[8px] border-2 transition min-h-[44px] ${
              value === p.id
                ? 'border-[#1A4A8A] bg-[#EFF6FF]'
                : 'border-[#E5E7EB] hover:border-[#9CA3AF]'
            }`}
          >
            <div
              className="w-10 h-7 rounded-[3px] border border-[#D1D5DB]"
              style={patternPreviewStyle(p.id, c1, c2)}
            />
            <span className={`text-[11px] font-medium whitespace-nowrap ${value === p.id ? 'text-[#1A4A8A]' : 'text-[#6B7280]'}`}>
              {p.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const JERSEY_PATH = 'M 8,8 L 22,18 Q 36,8 50,12 Q 64,8 78,18 L 92,8 L 100,30 L 82,42 L 78,110 L 22,110 L 18,42 L 0,30 Z';

function KitModel({ jerseyColour, jerseyColour2, jerseyPattern, shortsColour, socksColour, uid }) {
  const c1  = colourToHex(jerseyColour)  || '#E5E7EB';
  const c2  = colourToHex(jerseyColour2) || '#FFFFFF';
  const sc  = colourToHex(shortsColour)  || '#E5E7EB';
  const sk  = colourToHex(socksColour)   || '#E5E7EB';
  const pat = jerseyPattern || 'plain';

  return (
    <svg viewBox="0 0 100 220" width="88" height="193" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={`jclip-${uid}`}>
          <path d={JERSEY_PATH} />
        </clipPath>
        {pat === 'stripes' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="5" height="10" fill={c1} />
            <rect x="5" y="0" width="5" height="10" fill={c2} />
          </pattern>
        )}
        {pat === 'hoops' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="10" height="5" fill={c1} />
            <rect x="0" y="5" width="10" height="5" fill={c2} />
          </pattern>
        )}
      </defs>

      {/* Jersey fill */}
      {pat === 'plain' && <path d={JERSEY_PATH} fill={c1} />}
      {(pat === 'stripes' || pat === 'hoops') && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0" y="0" width="100" height="115" fill={`url(#jpat-${uid})`} />
        </g>
      )}
      {pat === 'halves' && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0"  y="0" width="50" height="115" fill={c1} />
          <rect x="50" y="0" width="50" height="115" fill={c2} />
        </g>
      )}
      {/* Jersey outline */}
      <path d={JERSEY_PATH} fill="none" stroke="#9CA3AF" strokeWidth="1.5" />

      {/* Shorts */}
      <rect x="24" y="116" width="52" height="48" rx="4" fill={sc} stroke="#9CA3AF" strokeWidth="1.5" />

      {/* Socks */}
      <rect x="26" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="54" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
    </svg>
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
    >
      {children}
    </select>
  );
}

function YearsOfBirthInput({ values, onChange }) {
  const [inputVal, setInputVal] = useState('');

  function addYear() {
    const y = inputVal.trim();
    const n = Number(y);
    if (!y || isNaN(n) || n < 1990 || n > 2025) return;
    if (values.includes(y)) { setInputVal(''); return; }
    onChange([...values, y].sort((a, b) => a - b));
    setInputVal('');
  }

  function removeYear(y) {
    onChange(values.filter((v) => v !== y));
  }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="number" min="1990" max="2025"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addYear())}
          placeholder="e.g. 2010"
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        />
        <button
          type="button"
          onClick={addYear}
          className="px-3 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition"
        >
          Add
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((y) => (
            <span key={y} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[13px] font-semibold text-[#1A4A8A]">
              {y}
              <button type="button" onClick={() => removeYear(y)} className="text-[#93C5FD] hover:text-[#1A4A8A] transition leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamInfoForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [tourInfo, setTourInfo]   = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');

  const [numSquads, setNumSquads]               = useState(1);
  const [goalkeepersTotal, setGoalkeepersTotal] = useState('');
  const [squads, setSquads]                     = useState([blankSquad(0)]);

  useEffect(() => {
    if (!token) { setError('Invalid or missing link.'); setLoading(false); return; }
    getDoc(doc(db, 'tourFormTokens', token)).then((snap) => {
      if (!snap.exists() || snap.data().type !== 'team_info') {
        setError('This link is invalid or has expired. Please contact MST Sports Tours.'); setLoading(false); return;
      }
      const d = snap.data();
      setTourInfo({ requestId: d.requestId, sport: d.sport || '', destination: d.destination || '', contactName: d.contactName || '', departureDate: d.departureDate || '', returnDate: d.returnDate || '', groupType: d.groupType || '' });
      setLoading(false);
    }).catch(() => { setError('Failed to load. Please try again.'); setLoading(false); });
  }, [token]);

  function updateSquadCount(n) {
    setNumSquads(n);
    setSquads((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(blankSquad(next.length));
      return next.slice(0, n);
    });
  }

  function updateSquad(i, field, value) {
    setSquads((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    for (let i = 0; i < numSquads; i++) {
      const sq = squads[i];
      if (!sq.gender)                  { setFormError(`Please select a gender for ${sq.label}.`); return; }
      if (!sq.yearsOfBirth?.length)    { setFormError(`Please enter at least one year of birth for ${sq.label}.`); return; }
      if (!sq.playersPerMatch)         { setFormError(`Please enter preferred players per match for ${sq.label}.`); return; }
      if (!sq.abilityLevel)    { setFormError(`Please select an ability level for ${sq.label}.`); return; }
      if (!sq.homeJerseyColour || !sq.homeShortsColour || !sq.homeSocksColour) {
        setFormError(`Please enter home kit colours for ${sq.label}.`); return;
      }
      if (sq.homeJerseyPattern !== 'plain' && !sq.homeJerseyColour2) {
        setFormError(`Please enter a second jersey colour for the ${sq.label} home kit.`); return;
      }
      if (!sq.onlyOneKit && (!sq.awayJerseyColour || !sq.awayShortsColour || !sq.awaySocksColour)) {
        setFormError(`Please enter away kit colours for ${sq.label}, or tick "Only one kit".`); return;
      }
      if (!sq.onlyOneKit && sq.awayJerseyPattern !== 'plain' && !sq.awayJerseyColour2) {
        setFormError(`Please enter a second jersey colour for the ${sq.label} away kit.`); return;
      }
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'requests', tourInfo.requestId, 'teamInfo', 'data'), {
        formToken: token,
        numSquads,
        goalkeepersTotal: goalkeepersTotal ? Number(goalkeepersTotal) : null,
        squads: squadsWithIds(squads.slice(0, numSquads)),
        submittedAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <p className="text-[15px] font-semibold text-[#111827] mb-2">Link not valid</p>
          <p className="text-[13px] text-[#6B7280]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">Team info received!</p>
          <p className="text-[13px] text-[#6B7280]">Thank you — your squad details have been saved. MST Sports Tours will be in touch to confirm your fixtures and sessions.</p>
        </div>
      </div>
    );
  }

  function formatDate(str) {
    if (!str) return '';
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }

  function KitSection({ sq, i, kitType }) {
    const jerseyColourField  = `${kitType}JerseyColour`;
    const jerseyColour2Field = `${kitType}JerseyColour2`;
    const jerseyPatternField = `${kitType}JerseyPattern`;
    const shortsColourField  = `${kitType}ShortsColour`;
    const socksColourField   = `${kitType}SocksColour`;

    return (
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="flex-1 space-y-3 min-w-0">
          <PatternSelector
            value={sq[jerseyPatternField]}
            onChange={(val) => updateSquad(i, jerseyPatternField, val)}
            colour1={sq[jerseyColourField]}
            colour2={sq[jerseyColour2Field]}
          />
          <ColourInput
            label="Jersey"
            required
            value={sq[jerseyColourField]}
            onChange={(val) => updateSquad(i, jerseyColourField, val)}
          />
          {sq[jerseyPatternField] !== 'plain' && (
            <ColourInput
              label="Jersey colour 2"
              required
              value={sq[jerseyColour2Field]}
              onChange={(val) => updateSquad(i, jerseyColour2Field, val)}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <ColourInput
              label="Shorts"
              required
              value={sq[shortsColourField]}
              onChange={(val) => updateSquad(i, shortsColourField, val)}
            />
            <ColourInput
              label="Socks"
              required
              value={sq[socksColourField]}
              onChange={(val) => updateSquad(i, socksColourField, val)}
            />
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col items-center pt-1">
          <p className="text-[11px] text-[#9CA3AF] font-medium mb-1">Preview</p>
          <KitModel
            uid={`${kitType}-${i}`}
            jerseyColour={sq[jerseyColourField]}
            jerseyColour2={sq[jerseyColour2Field]}
            jerseyPattern={sq[jerseyPatternField]}
            shortsColour={sq[shortsColourField]}
            socksColour={sq[socksColourField]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        {(tourInfo.groupType || '').trim().toUpperCase() === 'LLS' ? (
          <div className="rounded-[12px] mb-6 overflow-hidden relative">
            <img src="/LLS Banner.jpeg" alt="LLS" className="w-full h-auto block" />
            <div className="absolute inset-x-0 bottom-0 pt-20 pb-4 px-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent text-center">
              <p className="text-white font-bold text-[18px]">Team Information Sheet</p>
            </div>
          </div>
        ) : (
          <div className="bg-[#0D2240] rounded-[12px] p-6 mb-6 text-center">
            <p className="text-white font-bold text-[18px]">MST Sports Tours</p>
            <p className="text-[#93C5FD] text-[13px] mt-1">Team Information Sheet</p>
          </div>
        )}

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 mb-6">
          <p className="text-[13px] font-semibold text-[#111827]">{tourInfo.sport} — {tourInfo.destination}</p>
          {tourInfo.departureDate && (
            <p className="text-[12px] text-[#6B7280] mt-0.5">{formatDate(tourInfo.departureDate)} – {formatDate(tourInfo.returnDate)}</p>
          )}
          <p className="text-[12px] text-[#9CA3AF] mt-2">
            Please complete the details below to help us arrange the right fixtures and training sessions for your group.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Number of squads */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label required>How many squads?</Label>
                <Select value={numSquads} onChange={(e) => updateSquadCount(Number(e.target.value))}>
                  {SQUAD_COUNTS.map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </div>
              <div>
                <Label>Number of goalkeepers (overall)</Label>
                <Input
                  type="number" min="0" value={goalkeepersTotal}
                  onChange={(e) => setGoalkeepersTotal(e.target.value)}
                  placeholder="e.g. 4"
                />
              </div>
            </div>
          </div>

          {/* Per-squad sections */}
          {squads.slice(0, numSquads).map((sq, i) => (
            <div key={i} className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
              <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] px-5 py-3">
                <p className="text-[13px] font-semibold text-[#111827]">{sq.label}</p>
              </div>
              <div className="p-5 space-y-4">

                {/* Squad details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label required>Gender</Label>
                    <Select value={sq.gender} onChange={(e) => updateSquad(i, 'gender', e.target.value)}>
                      <option value="">Select…</option>
                      {GENDERS.map((g) => <option key={g}>{g}</option>)}
                    </Select>
                  </div>
                  <div>
                    <Label required>Year(s) of birth</Label>
                    <YearsOfBirthInput
                      values={sq.yearsOfBirth || []}
                      onChange={(years) => updateSquad(i, 'yearsOfBirth', years)}
                    />
                  </div>
                  <div>
                    <Label required>Preferred players per match</Label>
                    <Input
                      type="number" min="1" max="30" value={sq.playersPerMatch}
                      onChange={(e) => updateSquad(i, 'playersPerMatch', e.target.value)}
                      placeholder="e.g. 11"
                    />
                  </div>
                  <div>
                    <Label required>Ability level</Label>
                    <Select value={sq.abilityLevel} onChange={(e) => updateSquad(i, 'abilityLevel', e.target.value)}>
                      <option value="">Select…</option>
                      {ABILITIES.map((a) => <option key={a}>{a}</option>)}
                    </Select>
                  </div>
                </div>

                {/* Kit section */}
                <div className="pt-1 space-y-4">
                  <div>
                    <p className="text-[12px] font-semibold text-[#9CA3AF] uppercase tracking-[0.8px] mb-3">Home Kit</p>
                    <KitSection sq={sq} i={i} kitType="home" />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!sq.onlyOneKit}
                      onChange={(e) => updateSquad(i, 'onlyOneKit', e.target.checked)}
                      className="w-4 h-4 accent-[#1A4A8A]"
                    />
                    <span className="text-[13px] text-[#374151]">Only one kit</span>
                  </label>

                  {!sq.onlyOneKit && (
                    <div>
                      <p className="text-[12px] font-semibold text-[#9CA3AF] uppercase tracking-[0.8px] mb-3">Away Kit</p>
                      <KitSection sq={sq} i={i} kitType="away" />
                    </div>
                  )}
                </div>

              </div>
            </div>
          ))}

          {formError && (
            <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-4 py-3">{formError}</p>
          )}

          <button
            type="submit" disabled={saving}
            className="w-full py-3 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[14px] rounded-[10px] transition disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Submit Team Information'}
          </button>
        </form>
      </div>
    </div>
  );
}
